import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { IntegrityScore } from '../components/IntegrityScore';
import { RiskBadge } from '../components/RiskBadge';
import { SkeletonList } from '../components/Skeletons';
import { Users, AlertCircle, Loader2, FileText } from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import ExplainButton from '../components/ExplainButton';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Compare = () => {
  const { token, isPro, user } = useAuth();
  const navigate = useNavigate();
  const [candidates, setCandidates] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [comparisonData, setComparisonData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [comparing, setComparing] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);

  useEffect(() => {
    loadCandidates();
  }, []);

  const loadCandidates = async () => {
    try {
      const response = await axios.get(`${API}/candidates`, { timeout: 15000 });
      setCandidates(response.data);
    } catch (error) {
      console.error('Error loading candidates:', error);
      toast.error('Erro ao carregar candidatos');
    } finally {
      setLoading(false);
    }
  };

  const toggleSelection = (id) => {
    if (selectedIds.includes(id)) {
      setSelectedIds(selectedIds.filter(sid => sid !== id));
    } else {
      if (selectedIds.length >= 3) {
        toast.error('Maximo de 3 candidatos para comparacao');
        return;
      }
      setSelectedIds([...selectedIds, id]);
    }
  };

  const handleCompare = async () => {
    if (selectedIds.length < 2) {
      toast.error('Selecione pelo menos 2 candidatos');
      return;
    }

    setComparing(true);
    try {
      const response = await axios.post(`${API}/candidates/compare`, {
        candidate_ids: selectedIds
      }, { timeout: 15000 });
      setComparisonData(response.data);
      toast.success('Comparacao realizada!');
    } catch (error) {
      console.error('Error comparing candidates:', error);
      toast.error('Erro ao comparar candidatos');
    } finally {
      setComparing(false);
    }
  };

  const handleExportComparisonPDF = async () => {
    if (!user) { toast.info('Faça login para exportar em PDF.'); navigate('/auth'); return; }
    if (!isPro) {
      toast.info('Exportação em PDF é exclusiva do Plano Anual (R$ 14,90).');
      navigate('/planos');
      return;
    }
    setPdfLoading(true);
    try {
      const resp = await axios.post(`${API}/candidates/compare/pdf`, {
        candidate_ids: selectedIds,
      }, {
        responseType: 'blob',
        timeout: 30000,
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true,
      });
      const url = URL.createObjectURL(new Blob([resp.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'todeolho-comparacao.pdf';
      a.click();
      URL.revokeObjectURL(url);
      toast.success('PDF de comparação exportado!');
    } catch (err) {
      const status = err?.response?.status;
      if (status === 402 || status === 401) {
        toast.info('Ative o Plano Anual (R$ 14,90) para liberar a exportação em PDF.');
        navigate('/planos');
      } else {
        toast.error('Erro ao exportar PDF');
      }
    } finally {
      setPdfLoading(false);
    }
  };

  return (
    <div className="space-y-6 md:space-y-8">
      <div className="text-center py-6 md:py-8">
        <h1 className="text-3xl sm:text-4xl font-black text-[#0F1C2E] tracking-tight mb-3" data-testid="compare-title">
          Comparar Candidatos
        </h1>
        <p className="text-base text-[#475569]">
          Selecione ate 3 candidatos para comparar lado a lado
        </p>
      </div>

      <Card className="p-5 md:p-6 bg-white border border-[#E2E8F0] rounded-md">
        <h3 className="text-lg font-bold text-[#0F1C2E] mb-4">Candidatos Disponiveis</h3>

        {loading && <SkeletonList count={3} />}

        {!loading && candidates.length === 0 && (
          <div className="text-center py-12" data-testid="compare-empty">
            <AlertCircle className="w-12 h-12 text-[#94A3B8] mx-auto mb-4" />
            <p className="text-[#475569] mb-2">Nenhum candidato analisado ainda.</p>
            <p className="text-sm text-[#94A3B8]">Faca uma analise na pagina inicial primeiro.</p>
          </div>
        )}

        {!loading && candidates.length > 0 && (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-5">
              {candidates.map((candidate) => (
                <div
                  key={candidate.id}
                  data-testid={`candidate-select-${candidate.id}`}
                  className={`p-4 border-2 rounded-md cursor-pointer transition-all duration-200 ${
                    selectedIds.includes(candidate.id)
                      ? 'border-[#1E3A5F] bg-[#F4F6F8]'
                      : 'border-[#E2E8F0] hover:border-[#94A3B8]'
                  }`}
                  onClick={() => toggleSelection(candidate.id)}
                >
                  <div className="flex items-start gap-3">
                    <Checkbox checked={selectedIds.includes(candidate.id)} className="mt-1" />
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-[#0F1C2E] text-sm truncate">{candidate.name}</p>
                      <p className="text-xs text-[#94A3B8] mb-2 truncate">{candidate.position} - {candidate.state}</p>
                      <RiskBadge risk={candidate.risk_classification} className="text-[10px]" />
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <Button
              onClick={handleCompare}
              disabled={selectedIds.length < 2 || comparing}
              data-testid="compare-button"
              className="w-full bg-[#1E3A5F] hover:bg-[#152B47] text-white font-medium py-3 rounded-md"
            >
              {comparing ? (
                <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Comparando...</>
              ) : (
                <><Users className="w-5 h-5 mr-2" /> Comparar Selecionados ({selectedIds.length}/3)</>
              )}
            </Button>
          </>
        )}
      </Card>

      {/* Comparison Table */}
      {comparisonData && comparisonData.candidates.length > 0 && (
        <Card className="p-5 md:p-6 bg-white border border-[#E2E8F0] rounded-md" data-testid="comparison-table">
          <div className="flex items-center justify-between mb-5">
            <h3 className="text-lg font-bold text-[#0F1C2E]">Resultado da Comparacao</h3>
            <Button
              variant="outline"
              size="sm"
              onClick={handleExportComparisonPDF}
              disabled={pdfLoading}
              data-testid="export-comparison-pdf-btn"
              className="border-[#1E3A5F] text-[#1E3A5F] hover:bg-[#F4F6F8]"
            >
              {pdfLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileText className="w-4 h-4 mr-2" />}
              Exportar PDF
            </Button>
          </div>
          <div className="overflow-x-auto -mx-5 md:-mx-6 px-5 md:px-6">
            <table className="w-full border-collapse min-w-[500px]">
              <thead>
                <tr className="border-b-2 border-[#1E3A5F]">
                  <th className="text-left p-3 font-bold text-[#0F1C2E] text-sm">Criterio</th>
                  {comparisonData.candidates.map((c) => (
                    <th key={c.id} className="text-left p-3 font-bold text-[#0F1C2E] text-sm">{c.name}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {[
                  { label: 'Cargo', render: (c) => c.position },
                  { label: 'Partido', render: (c) => c.current_party },
                  {
                    label: 'Score',
                    render: (c) => <IntegrityScore score={c.integrity_score} size="sm" />,
                    explain: (c) => ({
                      contexto: `nota de integridade do candidato ${c.name}`,
                      valor: `${(c.integrity_score || 0).toFixed(1)} de 10`,
                      tipo: 'percentual',
                      fonte: 'Tô de Olho, com base em dados oficiais do TSE',
                      entidade: c.name,
                    }),
                  },
                  {
                    label: 'Risco',
                    render: (c) => <RiskBadge risk={c.risk_classification} />,
                    explain: (c) => ({
                      contexto: `classificação de risco do candidato ${c.name}`,
                      valor: c.risk_classification || 'Não classificado',
                      tipo: 'generico',
                      fonte: 'Tô de Olho, com base em dados oficiais do TSE',
                      entidade: c.name,
                    }),
                  },
                  {
                    label: 'Ficha Limpa',
                    render: (c) => (
                      <span className={`px-2 py-1 rounded-full text-xs font-bold ${
                        c.legal_status?.ficha_limpa_compliant ? 'bg-[#E8F5E9] text-[#2E7D32]' : 'bg-[#FFEBEE] text-[#C62828]'
                      }`}>
                        {c.legal_status?.ficha_limpa_compliant ? 'SIM' : 'NAO'}
                      </span>
                    ),
                    explain: (c) => ({
                      contexto: `situação Ficha Limpa do candidato ${c.name}`,
                      valor: c.legal_status?.ficha_limpa_compliant ? 'Compatível com Ficha Limpa' : 'Não compatível com Ficha Limpa',
                      tipo: 'generico',
                      fonte: 'Lei Complementar 135 de 2010, com base em dados do TSE',
                      entidade: c.name,
                    }),
                  },
                  {
                    label: 'Elegibilidade',
                    render: (c) => c.legal_status?.eligibility_status || 'N/A',
                    explain: (c) => ({
                      contexto: `situação de elegibilidade do candidato ${c.name}`,
                      valor: c.legal_status?.eligibility_status || 'Não informada',
                      tipo: 'generico',
                      fonte: 'Tribunal Superior Eleitoral',
                      entidade: c.name,
                    }),
                  },
                ].map((row, idx) => (
                  <tr key={row.label} className={`border-b border-[#E2E8F0] ${idx % 2 === 1 ? 'bg-[#F4F6F8]' : ''}`}>
                    <td className="p-3 font-semibold text-[#475569] text-sm">{row.label}</td>
                    {comparisonData.candidates.map((c) => (
                      <td key={c.id} className="p-3 text-sm text-[#475569]" data-testid={`compare-cell-${row.label.toLowerCase()}-${c.id}`}>
                        <div className="flex items-center gap-2 flex-wrap">
                          <span>{row.render(c)}</span>
                          {row.explain && (
                            <ExplainButton
                              {...row.explain(c)}
                              testId={`explain-compare-${row.label.toLowerCase().replace(/[^a-z]/g, '')}-${c.id}`}
                              variant="icon"
                            />
                          )}
                        </div>
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
};

export default Compare;
